import React from 'react';

const PROSE =
  'prose prose-sm max-w-none text-gray-800 [&_a]:text-blue-700';

function creditLine(credits) {
  if (!credits) return null;
  const parts = [];
  if (credits.source?.text) {
    parts.push(
      credits.source.url ? (
        <a key="source" href={credits.source.url} target="_blank" rel="noreferrer">
          {credits.source.text}
        </a>
      ) : (
        <span key="source">{credits.source.text}</span>
      )
    );
  }
  if (credits.author?.text) {
    parts.push(
      credits.author.url ? (
        <a key="author" href={credits.author.url} target="_blank" rel="noreferrer">
          {credits.author.text}
        </a>
      ) : (
        <span key="author">{credits.author.text}</span>
      )
    );
  }
  if (!parts.length) return null;
  return (
    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
      {parts.reduce((acc, el, i) => {
        if (i === 0) return [el];
        return [...acc, <span key={`sep-${i}`}> · </span>, el];
      }, [])}
    </div>
  );
}

function SectionPreview({ section }) {
  const data = section?.data || {};
  switch (section?.type) {
    case 'heading':
      return (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 20 + (6 - (data.level || 2)) * 2 }}>
            {data.text}
          </div>
          {data.html || data.html_updated ? (
            <div
              className={PROSE}
              dangerouslySetInnerHTML={{ __html: data.html_updated || data.html }}
            />
          ) : null}
        </div>
      );
    case 'paragraph':
    case 'rich_text':
      return (
        <div
          className={PROSE}
          style={{ marginBottom: 16 }}
          dangerouslySetInnerHTML={{ __html: data.html_updated || data.html || '' }}
        />
      );
    case 'border_cities_table': {
      const rows = Array.isArray(data.rows) ? data.rows : [];
      return (
        <div style={{ marginBottom: 20 }}>
          {data.title ? (
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>{data.title}</div>
          ) : null}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 14,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr style={{ background: '#0D0D65', color: '#fff' }}>
                <th style={{ width: '50%', padding: '10px 12px', textAlign: 'left' }}>
                  {data.left_header || ''}
                </th>
                <th style={{ width: '50%', padding: '10px 12px', textAlign: 'left' }}>
                  {data.right_header || ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td
                    style={{
                      width: '50%',
                      padding: '8px 12px',
                      borderTop: '1px solid #e5e7eb',
                      verticalAlign: 'top',
                    }}
                  >
                    {row.left}
                  </td>
                  <td
                    style={{
                      width: '50%',
                      padding: '8px 12px',
                      borderTop: '1px solid #e5e7eb',
                      verticalAlign: 'top',
                    }}
                  >
                    {row.right}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'image_block':
      return (
        <figure style={{ margin: '16px 0' }}>
          {data.url ? (
            <img
              src={data.url}
              alt={data.alt || ''}
              style={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 12 }}
            />
          ) : null}
          {data.caption ? (
            <figcaption style={{ textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 8 }}>
              {data.caption}
            </figcaption>
          ) : null}
          {creditLine(data.credits)}
        </figure>
      );
    case 'carousel': {
      const slides = Array.isArray(data.slides) ? data.slides : [];
      const first = slides[0] || {};
      const cities = Array.isArray(first.cities) ? first.cities : [];
      return (
        <div
          style={{
            marginBottom: 24,
            border: '1px solid #e4e4ed',
            borderRadius: 12,
            padding: 16,
            background: '#fafafa',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: '#2020A0', marginBottom: 8 }}>
            CAROUSEL · {slides.length} state(s)
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {slides.map((s, i) => (
              <span
                key={s.id || i}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: i === 0 ? '#2020A0' : '#fff',
                  color: i === 0 ? '#fff' : '#1a1a2e',
                  border: i === 0 ? 'none' : '1px solid #e4e4ed',
                }}
              >
                {s.badge || `State ${i + 1}`}
              </span>
            ))}
          </div>
          {first.eyebrow ? (
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2020A0', letterSpacing: 1 }}>
              {first.eyebrow}
            </div>
          ) : null}
          {first.title ? (
            <div style={{ fontWeight: 800, fontSize: 18, margin: '4px 0 8px', color: '#1a1a2e' }}>
              {first.title}
            </div>
          ) : null}
          {first.description ? (
            <p style={{ fontSize: 13, color: '#555560', margin: '0 0 12px' }}>{first.description}</p>
          ) : null}
          {cities.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {cities.map((c, i) => (
                <span
                  key={c.id || i}
                  style={{
                    fontSize: 11,
                    fontWeight: i === 0 ? 600 : 500,
                    padding: '5px 12px',
                    borderRadius: 999,
                    background: i === 0 ? 'rgba(32,32,160,0.1)' : '#fff',
                    color: i === 0 ? '#2020A0' : '#555560',
                    border: i === 0 ? '1px solid #2020A0' : '1px solid #e4e4ed',
                  }}
                >
                  {c.badge || `City ${i + 1}`}
                </span>
              ))}
            </div>
          ) : null}
          {cities[0] ? (
            <div style={{ textAlign: 'center', paddingTop: 8 }}>
              {cities[0].eyebrow ? (
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6ee58d', letterSpacing: 1 }}>
                  {cities[0].eyebrow}
                </div>
              ) : null}
              {cities[0].title ? (
                <div style={{ fontWeight: 800, fontSize: 15, margin: '4px 0' }}>{cities[0].title}</div>
              ) : null}
              {cities[0].description ? (
                <p style={{ fontSize: 12, color: '#b8b8bd', margin: 0 }}>{cities[0].description}</p>
              ) : null}
              {(cities[0].entries || []).length > 0 ? (
                <ul style={{ textAlign: 'left', fontSize: 12, marginTop: 12, paddingLeft: 18 }}>
                  {(cities[0].entries || []).map((entry, i) => (
                    <li key={entry.id || i}>
                      {entry.mobile_name || entry.custom_title || entry.port_name || entry.link_title || 'Crossing'}
                      {entry.port_number ? ` (${entry.port_number})` : ''}{' '}
                      {entry.image?.url ? ' · custom image' : ''}{' '}
                      {entry.link_url ? (
                        <span style={{ color: '#2020A0' }}>{entry.link_url}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : cities[0].link_url ? (
                <div style={{ fontSize: 12, color: '#2020A0', marginTop: 8 }}>{cities[0].link_url}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }
    default:
      return null;
  }
}

export default function PreviewCountry({ sections = [], hero = {}, faqs_section, full_slug }) {
  return (
    <div
      style={{
        maxWidth: 760,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        {full_slug || '/{lang}/{country}'}
      </div>
      {hero.eyebrow ? (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: '#2020A0',
            marginBottom: 8,
          }}
        >
          {hero.eyebrow}
        </div>
      ) : null}
      <h1 style={{ margin: '0 0 8px', color: '#0D0D65', fontSize: 28 }}>
        {hero.title || 'Country home'}
      </h1>
      {hero.description ? (
        <p style={{ color: '#374151', marginTop: 0 }}>{hero.description}</p>
      ) : null}
      {hero.image?.url ? (
        <img
          src={hero.image.url}
          alt={hero.image.alt || ''}
          style={{ width: '100%', borderRadius: 12, marginBottom: 24, maxHeight: 280, objectFit: 'cover' }}
        />
      ) : null}
      <div
        style={{
          padding: 12,
          background: '#f3f4f6',
          borderRadius: 8,
          marginBottom: 24,
          fontSize: 13,
          color: '#4b5563',
        }}
      >
        Fixed product sections (live wait times, border cards, etc.) render here in the public app.
      </div>
      {(sections || []).map((section, i) => (
        <SectionPreview key={section.id || i} section={section} />
      ))}
      {faqs_section?.items?.length > 0 && (
        <div style={{ marginTop: 32, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <h3 style={{ color: '#0D0D65' }}>{faqs_section.h3 || 'FAQs'}</h3>
          {faqs_section.p ? <p style={{ color: '#6b7280' }}>{faqs_section.p}</p> : null}
          {faqs_section.items.map((faq, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <strong>{faq.question}</strong>
              <div style={{ color: '#374151', marginTop: 4 }}>{faq.answer}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
